import React, { useEffect } from 'react';
import Popup from 'reactjs-popup';
import { toast } from 'react-toastify';
import { Card, Container, Row, Col, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle, faMultiply } from '@fortawesome/free-solid-svg-icons';
import QuoteCostBreakdown from './QuoteCostBreakdown';
import Button from '../Parts/Button';
import Money from '../Parts/Money';
import { useQuoteById } from '../Store/entities/quotes/hooks';
import apiCalls from '../PowerShadesAPIFunctions';
import { UsePortalDispatch } from '../Store';
import { RefreshQuotePricing } from '../Store/entities/quotes';

type SalesApprovalModalProps = {
	isOpen: boolean;
	onClose: () => void;
	quoteId: number;
};

const SalesApprovalModal = ({ isOpen, onClose, quoteId }: SalesApprovalModalProps) => {
	const [quoteCostBreakdownOpen, setQuoteCostBreakdownOpen] = React.useState(false);

	const quote = useQuoteById(quoteId);

	const onCloseHandler = () => {
		if (!quoteCostBreakdownOpen) {
			onClose();
		}
		setQuoteCostBreakdownOpen(false);
	};

	const { cost_snapshot: costSnapshot } = useQuoteById(quoteId);
	const [invoiceTotal, setInvoiceTotal] = React.useState<null | number>(null);

	const isInvoiceLow = (invoiceTotal ?? 0) < (costSnapshot ?? 0);
	const isDistLow = (quote?.dist_sale_price_snapshot ?? 0) < (costSnapshot ?? 0);
	const isDealerLow = (quote?.dealer_sale_price_snapshot ?? 0) < (costSnapshot ?? 0);


	useEffect(() => {
		apiCalls.getQuoteInvoiceSnapshot(quoteId).then((resp) => {
			setInvoiceTotal(resp.data?.invoice?.total !== undefined ? Number(resp.data.invoice.total) : null);
		});
	}, [quoteId]);

	const successOrErrorCssClassInvoice = isInvoiceLow && invoiceTotal !== null ? 'text-danger' : 'text-primary';
	const successOrErrorCssClassDist = isDistLow ? 'text-danger' : 'text-primary';
	const successOrErrorCssClassDealer = isDealerLow ? 'text-danger' : 'text-primary';

	const dispatch = UsePortalDispatch();

	useEffect(() => {
		if (isOpen) {
			dispatch(RefreshQuotePricing({ quoteId }));
		}
	}, [isOpen]);





	return (
		<Popup
			closeOnDocumentClick={false}
			closeOnEscape={false}
			className="mobile-modal"
			open={isOpen}
			modal
			onClose={onCloseHandler}
		>
			{quoteCostBreakdownOpen ? (
				<QuoteCostBreakdown
					quoteId={quoteId}
					onClose={() => {
						setQuoteCostBreakdownOpen(false);
					}}
				/>
			) : (
				<Container fluid className="p-0">
					<Card className="border-0 shadow-sm">
						<div className=' d-flex align-items-start position-relative'>
							<FontAwesomeIcon
								icon={faMultiply}
								className="close-icon link-secondary"
								onClick={onClose}
								style={{ marginRight: '1em', flex: '0 0 auto' }}
								cursor='pointer'
							/>
							<h4 style={{ flex: 1, textAlign: 'center', margin: 0 }}>Sales Approval Request</h4>
							<div style={{ marginLeft: '1em', flex: '0 0 auto', width: '1em' }} />
						</div>
						<Card.Body className="p-4">
							<Row className="mb-4">
								<Col>
									<div className="alert alert-info d-flex align-items-center">
										<FontAwesomeIcon icon={faInfoCircle} className="me-2" />
										<span>
											Please review the pricing details below before approving this quote.
										</span>
									</div>
								</Col>
							</Row>
							<Row className="g-3">
								<Col md={4}>
									<Card className="border-success h-100">
										<Card.Body className="text-center">
											<h6 className="text-muted mb-2">Our Cost</h6>
											<h4 className="text-success mb-0">
												<Money>{costSnapshot}</Money>
											</h4>
										</Card.Body>
									</Card>
								</Col>

								<Col md={4}>
									<Card className="border-primary h-100">
										<Card.Body className="text-center">
											<h6 className="text-muted mb-2">Invoice Total</h6>
											<h4 title="If the number is lower than the other snapshots, it's because the price was overriden." className={`mb-0 ${successOrErrorCssClassInvoice}`}>
												{invoiceTotal !== null ? (<Money>{invoiceTotal}</Money>) : "Loading..."}
											</h4>
										</Card.Body>
									</Card>
								</Col>
								<Col md={4}>
									<Card className="border-primary h-100">
										<Card.Body className="text-center">
											<h6 className="text-muted mb-2">Distributor Price</h6>
											<h4 className={`mb-0 ${successOrErrorCssClassDist}`}>
												<Money>{quote.dist_sale_price_snapshot}</Money>
											</h4>
										</Card.Body>
									</Card>
								</Col>

								<Col md={4}>
									<Card className="border-primary h-100">
										<Card.Body className="text-center">
											<h6 className="text-muted mb-2">Dealer Price</h6>
											<h4 className={`mb-0 ${successOrErrorCssClassDealer}`}>
												<Money>{quote.dealer_sale_price_snapshot}</Money>
											</h4>
										</Card.Body>
									</Card>
								</Col>
							</Row>

							<Row className="mt-4">
								<Col>
									<Card className="bg-body-secondary">
										<Card.Body>
											<h6 className="mb-2">
												<FontAwesomeIcon icon={faInfoCircle} className="text-muted me-2" />
												Discount Reason
											</h6>
											{
												((quote.coupon_name ?? "") !== "") &&
												<Badge bg="secondary" className='fs-6 p-2'>
													{quote.coupon_name}
												</Badge>
											}
											{<Badge bg="secondary" className="fs-6 p-2">
												{quote.discount_reason && quote.discount_reason != "None Recorded" ? quote.discount_reason :( quote.coupon_name ? "" : "No reason provided")}
											</Badge>}
										</Card.Body>
									</Card>
								</Col>
							</Row>
						</Card.Body>

						<div className='d-flex justify-content-between px-4'>
							<Button
								color="light"
								onClick={() => setQuoteCostBreakdownOpen(true)}
							>
								View Cost Breakdown
							</Button>

							<Button
								color="green"
								onClick={() => {
									apiCalls.salesRejectOrder(quoteId)
										.then(() => {
											toast.success("Sales order rejected successfully!");
											onClose();
											window.location.reload();
										})
										.catch((error) => {
											toast.error(`Failed to reject sales order: ${error.message}`);
										});
								}}
							>
								Reject
							</Button>
							<Button
								color="green"
								onClick={() => {
									apiCalls.salesApproveOrder(quoteId)
										.then(() => {
											toast.success("Sales order approved successfully!");
											onClose();
											window.location.reload();
										})
										.catch((error) => {
											toast.error(`Failed to approve sales order: ${error.message}`);
										});
								}}
							>
								Approve
							</Button>
						</div>
					</Card>
				</Container>
			)}
		</Popup>
	);
};

export default SalesApprovalModal;
